import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Users, Target, Check } from "lucide-react";
import SpeelruimteBanner from "@/components/SpeelruimteBanner";
import { SquiggleUnderline, FloatingBlob } from "@/components/Squiggle";
import { PROGRAMMAS } from "@/data/programmaLogic";
import usePageMeta from "@/hooks/usePageMeta";

// De Programmabouwer — een gesprekstool, geen kostenoverzicht.
// Drie stappen: Cohort → Uitkomst → Berekening.

const COHORTS = [
  {
    k: "student",
    label: "Student Reset",
    ondertitel: "12 weken · mbo/hbo",
    programma: "start",
    icon: Users,
    tint: "#97B79D",
    ppKostBasis: 1950,
    min: 6,
    max: 30,
    beschrijving: "Voor studenten bij wie verzuim oploopt of motivatie verdwijnt.",
    kpiPrimair: "Terug in opleiding of concrete vervolgstap",
  },
  {
    k: "gemeente-start",
    label: "Gemeenten · LevelLeven START",
    ondertitel: "12 weken · 20–22 deelnemers",
    programma: "start",
    icon: Users,
    tint: "#E06B3F",
    ppKostBasis: 5000,
    min: 20,
    max: 24,
    beschrijving: "Voor inwoners die langdurig stilstaan, meerdere belemmeringen ervaren en onvoldoende aansluiting vinden bij bestaande trajecten.",
    kpiPrimair: "Eigen regie, vaardigheden, veerkracht en een passende vervolgstap",
  },
];

const UITKOMSTEN = [
  { k: "beweging", label: "Ontwikkeling in beeld", d: "Start-, tussen- en eindmeting op onder meer zelfregie, dagstructuur, vertrouwen en perspectief." },
  { k: "bereik", label: "Bereik en deelname", d: "Bereiken we inwoners die nu onvoldoende aansluiting vinden? Aanwezigheid, deelname en afronding." },
  { k: "vervolg", label: "Passende vervolgstap", d: "Aansluiting op participatie, scholing, begeleiding of werk via bestaande voorzieningen." },
];

const formatEuro = (n) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

export default function Programmabouwer() {
  usePageMeta({
    title: "Programmabouwer · LevelLeven",
    description:
      "Verken een LevelLeven START-pilot. Kies doelgroep en onderzoeksfocus en zie een indicatieve kostenraming.",
  });

  const [step, setStep] = useState(0);
  const [cohort, setCohort] = useState(null);
  const [uitkomst, setUitkomst] = useState(null);
  const [deelnemers, setDeelnemers] = useState(20);

  const berekening = useMemo(() => {
    if (!cohort || !uitkomst) return null;
    const n = Math.min(Math.max(deelnemers, cohort.min), cohort.max);
    return { n, totaal: cohort.ppKostBasis * n };
  }, [cohort, uitkomst, deelnemers]);

  return (
    <div className="bg-ll-cream text-ll-ink min-h-screen">
      <SpeelruimteBanner
        eyebrow="De Programmabouwer"
        headline={
          <>
            Bouw uw eigen{" "}
            <span className="italic text-ll-coral relative inline-block">
              LevelLeven
              <SquiggleUnderline color="#E06B3F" offset={-2} height={18} />
            </span>.
          </>
        }
        belofte="Een gesprekstool, geen kostenoverzicht."
        subhead="Kies doelgroep en wat u wilt onderzoeken. U ziet direct een indicatieve kostenraming. Geen beloofde besparingen: wat een pilot oplevert, gaan we samen meten."
        tone="coral"
        ctaPrimary={{ label: "Begin met bouwen", to: "#bouwer" }}
      />

      <section id="bouwer" className="py-20 sm:py-28 relative">
        <FloatingBlob color="#F0B98D" size={140} className="top-10 right-6 opacity-70" />
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16">
          {/* Steps indicator */}
          <div className="flex items-center gap-3 mb-14 justify-center">
            {["Cohort", "Uitkomst", "Berekening"].map((label, i) => (
              <div key={label} className="flex items-center gap-3">
                <span
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                    step >= i ? "bg-ll-ink text-ll-cream" : "bg-ll-ink/10 text-ll-ink/60"
                  }`}
                >
                  {i + 1}
                </span>
                <span
                  className={`text-xs uppercase tracking-[0.25em] ${
                    step >= i ? "text-ll-ink" : "text-ll-ink/50"
                  }`}
                >
                  {label}
                </span>
                {i < 2 && <span className="w-8 h-px bg-ll-ink/20" />}
              </div>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {/* STEP 0 · COHORT */}
            {step === 0 && (
              <motion.div key="step-cohort" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }}>
                <div className="max-w-2xl mx-auto text-center mb-12">
                  <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-tight font-light">
                    Voor wie{" "}
                    <span className="italic text-ll-coral">bouwt u</span>?
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
                  {COHORTS.map((c) => {
                    const selected = cohort?.k === c.k;
                    return (
                      <button
                        key={c.k}
                        type="button"
                        onClick={() => { setCohort(c); setDeelnemers(c.k === "student" ? 12 : c.k === "gemeente-start" ? 15 : 12); }}
                        data-testid={`bouwer-cohort-${c.k}`}
                        className={`p-8 rounded-3xl border-2 text-left transition-all ${selected ? "border-ll-ink bg-white" : "border-ll-ink/10 bg-white/60 hover:border-ll-ink/40"}`}
                      >
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: c.tint }}>
                          <c.icon size={18} className="text-ll-ink" strokeWidth={1.6} />
                        </div>
                        <p className="mt-5 text-[10px] uppercase tracking-[0.3em] text-ll-navy/60">{c.ondertitel}</p>
                        <p className="mt-2 font-heading text-2xl font-light">{c.label}</p>
                        <p className="mt-3 text-sm text-ll-navy/80">{c.beschrijving}</p>
                        <p className="mt-4 text-xs italic text-ll-coral">Primair KPI: {c.kpiPrimair}</p>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-12 text-center">
                  <button
                    type="button"
                    disabled={!cohort}
                    data-testid="bouwer-next-0"
                    onClick={() => setStep(1)}
                    className={`group inline-flex items-center gap-3 rounded-full pl-6 pr-2 py-2 transition-all ${cohort ? "bg-ll-ink text-ll-cream hover:bg-ll-coral" : "bg-ll-ink/10 text-ll-ink/40 cursor-not-allowed"}`}
                  >
                    <span className="text-base font-medium">Volgende stap</span>
                    <span className={`w-11 h-11 rounded-full flex items-center justify-center ${cohort ? "bg-ll-coral text-ll-ink" : "bg-ll-ink/10"}`}>
                      <ArrowUpRight size={18} />
                    </span>
                  </button>
                </div>
              </motion.div>
            )}

            {/* STEP 1 · UITKOMST */}
            {step === 1 && (
              <motion.div key="step-uitkomst" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }}>
                <div className="max-w-2xl mx-auto text-center mb-12">
                  <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-tight font-light">
                    Wat wilt u{" "}
                    <span className="italic text-ll-coral">onderzoeken</span>?
                  </h2>
                  <p className="mt-4 text-ll-navy/80">Dit zijn beoogde en te onderzoeken effecten, geen bewezen resultaten.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {UITKOMSTEN.map((u) => {
                    const selected = uitkomst?.k === u.k;
                    return (
                      <button
                        key={u.k}
                        type="button"
                        onClick={() => setUitkomst(u)}
                        data-testid={`bouwer-uitkomst-${u.k}`}
                        className={`p-8 rounded-3xl border-2 text-left transition-all ${selected ? "border-ll-ink bg-white" : "border-ll-ink/10 bg-white/60 hover:border-ll-ink/40"}`}
                      >
                        <Target size={22} strokeWidth={1.5} className={selected ? "text-ll-coral" : "text-ll-ink/60"} />
                        <p className="mt-5 font-heading text-2xl font-light">{u.label}</p>
                        <p className="mt-3 text-sm text-ll-navy/80">{u.d}</p>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-12 flex justify-between max-w-md mx-auto">
                  <button type="button" onClick={() => setStep(0)} className="text-xs uppercase tracking-[0.28em] text-ll-navy/60 hover:text-ll-ink">← Terug</button>
                  <button
                    type="button"
                    disabled={!uitkomst}
                    data-testid="bouwer-next-1"
                    onClick={() => setStep(2)}
                    className={`group inline-flex items-center gap-3 rounded-full pl-6 pr-2 py-2 transition-all ${uitkomst ? "bg-ll-ink text-ll-cream hover:bg-ll-coral" : "bg-ll-ink/10 text-ll-ink/40 cursor-not-allowed"}`}
                  >
                    <span className="text-base font-medium">Zie de kostenraming</span>
                    <span className={`w-11 h-11 rounded-full flex items-center justify-center ${uitkomst ? "bg-ll-coral text-ll-ink" : "bg-ll-ink/10"}`}>
                      <ArrowUpRight size={18} />
                    </span>
                  </button>
                </div>
              </motion.div>
            )}

            {/* STEP 2 · BEREKENING */}
            {step === 2 && cohort && uitkomst && berekening && (
              <motion.div key="step-berekening" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }}>
                <div className="max-w-3xl mx-auto text-center mb-10">
                  <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Jouw programma</p>
                  <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl leading-tight font-light">
                    {cohort.label} · <span className="italic text-ll-coral">{uitkomst.label}</span>
                  </h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                  <div className="lg:col-span-4 space-y-5">
                    <div className="p-6 rounded-2xl border border-ll-ink/10 bg-white">
                      <label className="text-[10px] uppercase tracking-[0.28em] text-ll-navy/60">Deelnemers in de groep</label>
                      <input
                        type="range"
                        min={cohort.min}
                        max={cohort.max}
                        value={berekening.n}
                        onChange={(e) => setDeelnemers(Number(e.target.value))}
                        data-testid="bouwer-slider-deelnemers"
                        className="w-full mt-3 accent-ll-coral"
                      />
                      <p className="mt-2 font-heading text-3xl">{berekening.n}</p>
                      {cohort.max === 24 && (
                        <p className="mt-2 text-xs text-ll-navy/60">Bij voorkeur 20–22, maximaal 24 waar verantwoord. Twee trainers per groep.</p>
                      )}
                    </div>
                  </div>
                  <div className="lg:col-span-8 rounded-3xl bg-ll-navy text-ll-cream p-8 sm:p-10">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.28em] text-ll-yellow">Indicatieve investering</p>
                        <p className="mt-3 chapter-num text-4xl sm:text-5xl" data-testid="bouwer-totaal">{formatEuro(berekening.totaal)}</p>
                        <p className="mt-2 text-sm text-ll-cream/70">
                          circa {formatEuro(cohort.ppKostBasis)} p.p. × {berekening.n} deelnemers
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.28em] text-ll-yellow">Onderzoeksfocus</p>
                        <p className="mt-3 font-heading text-2xl">{uitkomst.label}</p>
                        <p className="mt-2 text-sm text-ll-cream/70">{uitkomst.d}</p>
                      </div>
                    </div>
                    <p className="mt-8 pt-6 border-t border-ll-cream/15 text-xs text-ll-cream/60 italic leading-relaxed">
                      Indicatief. De definitieve prijs hangt af van de uitvoering en de afspraken met uw gemeente. We noemen geen besparingen of rendement: de effectiviteit van LevelLeven wordt nog onderzocht.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-4">
                      <Link
                        to="/contact?rol=gemeente"
                        data-testid="bouwer-cta-contact"
                        className="group inline-flex items-center gap-3 bg-ll-yellow text-ll-ink hover:bg-ll-coral hover:text-ll-cream rounded-full pl-6 pr-2 py-2 transition-all"
                      >
                        <span className="text-base font-medium">Bespreek deze opzet</span>
                        <span className="bg-ll-ink text-ll-cream w-11 h-11 rounded-full flex items-center justify-center">
                          <ArrowUpRight size={18} />
                        </span>
                      </Link>
                      <button
                        type="button"
                        onClick={() => { setStep(0); setCohort(null); setUitkomst(null); }}
                        data-testid="bouwer-reset"
                        className="inline-flex items-center gap-2 text-ll-cream/80 hover:text-ll-yellow transition-colors"
                      >
                        Bouw een andere opzet
                      </button>
                    </div>
                  </div>
                </div>
                <div className="mt-12 text-center">
                  <button type="button" onClick={() => setStep(1)} className="text-xs uppercase tracking-[0.28em] text-ll-navy/60 hover:text-ll-ink">← Terug naar uitkomst</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
}
